import clsx from 'clsx';
import React from 'react';

interface IInput {
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: any;
  error?: string | string[];
  name: string;
  label?: string;
  placeholder?: string;
  type?: string;
  rightIcon?: {
    icon: string | React.ReactNode;
    onClick?: () => void;
    color?: string;
  };
  leftIcon?: {
    icon: string | React.ReactNode;
    onClick?: () => void;
    color?: string;
  };
  min?: number | string;
  max?: number | string;
  step?: number | string;
  value?: string | number;
  rightIconBg?: string;
  leftIconBg?: string;
  className?: string;
  inputClassName?: string;
  readOnly?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
  isRequired?: boolean;
  integer?: boolean;
  maxlength?: number;
  rightHeaderIcon?: React.ReactNode;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  autoComplete?: string;
  dataLpignore?: string;
  dataFormType?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  inputStyle?: React.CSSProperties;
}

const Input = ({
  disabled,
  error,
  type = 'text',
  register,
  name,
  placeholder,
  rightIcon,
  leftIcon,
  rightHeaderIcon,
  min,
  max,
  step,
  value,
  label,
  rightIconBg,
  leftIconBg,
  className,
  inputClassName,
  readOnly,
  autoFocus = false,
  onBlur,
  onKeyDown,
  isRequired,
  integer,
  maxlength,
  onChange,
  autoComplete,
  dataLpignore,
  dataFormType,
  inputRef,
  inputStyle,
}: IInput) => {
  const registered = register?.(name, {
    valueAsNumber: type === 'number',
  });

  const isIntegerNumber = integer && type === 'number';
  const defaultStep = type === 'number' ? 0.01 : undefined;
  const effectiveStep = isIntegerNumber ? 1 : (step ?? defaultStep);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (isIntegerNumber && ['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
      return;
    }
    onKeyDown?.(e);
  };

  return (
    <div className={clsx('w-full flex flex-col gap-1.5', className)}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-neutral-primary leading-5"
        >
          <div className="flex justify-between items-center">
            <span>
              {label} {isRequired && <span className="text-red-500">*</span>}
            </span>
            {rightHeaderIcon ?? rightHeaderIcon}
          </div>
        </label>
      )}
      <div
        className={clsx(
          'group w-full rounded-lg flex shadow-s gap-2',
          disabled
            ? 'border border-neutral-light-200 bg-neutral-100 focus-within:outline-none'
            : error
              ? 'border border-error-default focus-within:outline-none bg-white'
              : 'border border-neutral-light-200 focus-within:outline-2 bg-white focus-within:outline-offset-2 hover:border-brand-default focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff] text-neutral-900 font-semibold text-base',
          {
            'border-neutral-light-200! focus-within:outline-none!': readOnly,
          }
        )}
      >
        {leftIcon?.icon && (
          <button
            onClick={leftIcon.onClick}
            type="button"
            className={clsx(
              'ps-3 rounded-e-lg flex items-center justify-center outline-none font-normal cursor-default!',
              rightIconBg ? `bg-[${leftIconBg}]` : ''
            )}
            style={{ color: leftIcon.color }}
            disabled={disabled}
          >
            {leftIcon.icon}
          </button>
        )}
        <input
          disabled={disabled}
          readOnly={readOnly}
          id={name}
          name={name}
          type={type}
          autoComplete={autoComplete}
          data-lpignore={dataLpignore}
          data-form-type={dataFormType}
          ref={inputRef}
          onInput={
            maxlength
              ? (e) => {
                  const input = e.target as HTMLInputElement;
                  if (input.value.length > maxlength) {
                    input.value = input.value.slice(0, maxlength);
                  }
                }
              : undefined
          }
          placeholder={placeholder}
          onWheel={(e) => e.currentTarget.blur()}
          value={register?.value || value}
          maxLength={maxlength}
          step={effectiveStep}
          onKeyDown={handleKeyDown}
          {...registered}
          onChange={(e) => {
            registered?.onChange?.(e);
            onChange?.(e);
          }}
          onBlur={(e) => {
            if (e.target.value) {
              e.target.value = e.target.value.trim().replace(/\s+/g, ' ');
            }
            registered?.onBlur?.(e); // RHF internal handler
            onBlur?.(e); // Your custom handler
          }}
          autoFocus={autoFocus}
          style={inputStyle}
          className={clsx(
            'w-full focus:outline-none border-none py-2.5 px-3 rounded-lg text-sm font-medium text-neutral-900',
            'placeholder:font-medium placeholder:text-neutral-disabled autofill:bg-transparent!',
            disabled &&
              'text-neutral-disabled cursor-not-allowed bg-neutral-50 focus:outline-none',
            leftIcon?.icon && 'ps-0!',
            inputClassName
          )}
          min={min}
          max={max}
        />
        {rightIcon?.icon && (
          <button
            onClick={rightIcon.onClick}
            type="button"
            className={clsx(
              'px-3 rounded-e-lg flex items-center justify-center outline-none min-w-10.25 font-normal'
            )}
            style={{ color: rightIcon.color, background: rightIconBg }}
            disabled={disabled}
          >
            {rightIcon.icon}
          </button>
        )}
      </div>
      {error &&
        (typeof error === 'object' ? (
          error.map((e, i) => (
            <p key={i} className="text-sm font-normal text-error-default">
              {e}
            </p>
          ))
        ) : (
          <p className="text-sm font-normal text-error-default">{error}</p>
        ))}
    </div>
  );
};

export default Input;
